<template>
    <div v-if="'completed_by_partner' === method">
        <FormGeolocationCompletedByPartner
            :index="props.index"
            :number="props.number"
            :reference="props.reference"
            :optionsCountry="props.optionsCountry"
        />
    </div>
    <div v-else>
        <FormKit
            name="manualMethod"
            v-model="method"
            type="radio"
            step="any"
            label="Méthode"
            :options="{
                address: 'Adresse',
                geocoding: 'Latitude/Longitude'                    
            }"
        />
        <div v-if="null !== warningRequestGeolocation" class="status-geolocation">
            <small class="warning-geolocation">{{ warningRequestGeolocation }}</small>
        </div>
        <div v-if="null !== errorRequestGeolocation" class="status-geolocation">
            <small class="error-geolocation">{{ errorRequestGeolocation }}</small>
        </div>
        <FormKit
            v-show="'address' === method"
            v-model="address"
            type="text"
            step="any"
            :label="props.number + '. Adresse*'"
            name="address"
            placeholder="Adresse"
            @keyup="inputAddress"
        />
        <FormKit
            v-show="'address' === method"
            v-model="zipCode"
            type="text"
            step="any"
            :label="props.number + '. Code Postal*'"
            name="zipCode"
            placeholder="Code Postal"
            @keyup="inputAddress"
        />
        <FormKit
            v-show="'address' === method"
            v-model="city"
            type="text"
            step="any"
            :label="props.number + '. Ville*'"
            name="city"
            placeholder="Ville"
            @keyup="inputAddress"
        />
        <FormKit
            v-show="'address' === method"
            v-model="country"
            type="select"
            :label="props.number + '. Pays*'"
            name="country"
            placeholder="Pays"
            :options="props.optionsCountry"
            @change="(event) => {
                // Ensure country is a code, not a full name
                country = ensureCountryCode(event.target ? event.target.value : event);
                inputAddress();
            }"
        />
        <FormKit
            v-show="'geocoding' === method"
            v-model="formattedAddress"
            disabled
            type="textarea"
            step="any"
            rows="2"
            :label="props.number + '. Adresse correspondant aux coordonnées*'"
            name="formattedAddress"
            placeholder="Adresse complète"
            validation="required"
            :validation-messages="validationMessages"
            validation-visibility="submit"
        />
        <div v-if="'address' === method && null !== addressFound" class="suggestion-formatted-address">
            <label class="formkit-label">Coordonnées pour l'adresse : </label>
            <div>
                <small>{{ addressFound }}</small>
            </div>
            <div class="suggestion-formatted-address-alert">
                <small>{{ `${t('ProductForm.Geolocation.alertVerificationAddress')}` }}</small>
            </div>
        </div>
        <FormKit
            :disabled="'address' === method"
            v-model="latitude"
            type="number"
            step="any"
            :label="props.number + '. Latitude*'"
            name="latitude"
            placeholder="Latitude"
            validation="required|min:-90|max:90|maximumDecimalsGeocoding"
            :validation-rules="{ maximumDecimalsGeocoding }"
            :validation-messages="validationMessages"
            validation-visibility="submit"
            @keyup="inputGeocode"
        />
        <FormKit
            :disabled="'address' === method"
            v-model="longitude"
            type="number"
            step="any"
            :label="props.number + '. Longitude*'"
            name="longitude"
            placeholder="Longititude"
            validation="required|min:-90|max:90|maximumDecimalsGeocoding"
            :validation-rules="{ maximumDecimalsGeocoding }"
            :validation-messages="validationMessages"
            validation-visibility="submit"
            @keyup="inputGeocode"
        />
        <FormKit
            :disabled="requestingGeolocation || (!activateRequestGeolocation && !props.isEditMode)"
            v-if="'geocoding' !== method"
            type="button"
            :label="requestingGeolocation ? `${t('ProductForm.Geolocation.actions.requestingGeolocation')}` : `${t('ProductForm.Geolocation.actions.getGeocode')}`"
            :class="{ 'btn-secondary': true, 'btn-block': true }"
            @click="getGeocodeByAddress"
        />
        <FormKit
            :disabled="requestingGeolocation || (!activateRequestGeolocation && !props.isEditMode)"
            v-if="'geocoding' === method"
            type="button"
            :label="requestingGeolocation ? `${t('ProductForm.Geolocation.actions.requestingGeolocation')}` : `${t('ProductForm.Geolocation.actions.getAddress')}`"
            :class="{ 'btn-secondary': true, 'btn-block': true }"
            @click="getAddressByGeocode"
        />
        
    </div>
</template>


<script setup lang="ts">
    import {ref, watch, computed, onMounted} from "vue";
    import ApiService from "@/_services/api.service";
    import { maximumDecimalsGeocoding } from "@/_services/geolocation/validations";
    import { useI18n } from "vue-i18n";
    import FormGeolocationCompletedByPartner from "@/components/forms/product/FormGeolocationCompletedByPartner.vue";
    import { useGeolocationStore } from "@/stores/geoloc/geolocationStore";
    
    const { t } = useI18n();
    const geolocationStore = useGeolocationStore();
    
    const validationMessages = {
        maximumDecimalsGeocoding: `${t('ProductForm.Geolocation.validationMessage.maximumDecimalsGeocoding')}`,
        required: `${t('ProductForm.Geolocation.validationMessage.required')}`,
        min: `${t('ProductForm.Geolocation.validationMessage.min')}`,
        max: `${t('ProductForm.Geolocation.validationMessage.max')}`
    };
    
    // Define Props
    const props = defineProps({
        'index': {
            type: Number
        },
        'number': {
            type: Number
        },
        'reference': {
            type: Object,
            default: () => ({
                manualMethod: null,
                address: null,
                zipCode: null,
                city: null,
                country: null,
                latitude: null,
                longitude: null
            })
        },
        'optionsCountry': {
            type: Object
        },
        'isEditMode': {
            type: Boolean,
            default: false
        }
    });
    
    // Define computed properties that use the store
    const method = computed({
        get: () => geolocationStore.manualMethod,
        set: (value) => geolocationStore.updateField('manualMethod', value)
    });
    
    const address = computed({
        get: () => geolocationStore.address,
        set: (value) => geolocationStore.updateField('address', value)
    });
    
    const zipCode = computed({
        get: () => geolocationStore.zipCode,
        set: (value) => geolocationStore.updateField('zipCode', value)
    });
    
    const city = computed({
        get: () => geolocationStore.city,
        set: (value) => geolocationStore.updateField('city', value)
    });
    
    const country = computed({
        get: () => geolocationStore.country,
        set: (value) => {
            // Ensure country is a code, not a full name
            const countryCode = ensureCountryCode(value);
            geolocationStore.updateField('country', countryCode);
        }
    });
    
    const latitude = computed({
        get: () => geolocationStore.latitude,
        set: (value) => geolocationStore.updateField('latitude', value)
    });
    
    const longitude = computed({
        get: () => geolocationStore.longitude,
        set: (value) => geolocationStore.updateField('longitude', value)
    });
    
    const formattedAddress = computed({
        get: () => geolocationStore.formattedAddress,
        set: (value) => geolocationStore.updateField('formattedAddress', value)
    });
    
    // Define refs
    let initialStatus = ref<string|null>( props.reference.status ?? '');
    let activateRequestGeolocation = ref(false);
    let requestingGeolocation = ref(false);
    let warningRequestGeolocation = ref<string|null>(null);
    let errorRequestGeolocation = ref<string|null>(null);
    let addressFound = ref<string|null>(null);

    // Initialize the store with the reference data
    onMounted(() => {
        // Pass the edit mode to the store
        if (props.isEditMode) {
            geolocationStore.updateField('isEditMode', true);
        }
        
        // Ensure country code is correct in the reference data
        if (props.reference && props.reference.country) {
            props.reference.country = ensureCountryCode(props.reference.country);
        }
        
        geolocationStore.initGeolocationForm(props.reference);
    });

    const isEmpty = (val) => {
        return (undefined === val || null === val || '' === val);
    }
    
    if ('geocode_not_found_by_address' === initialStatus.value) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetGeocodeByAddress')}`;
    }

    if ('address_not_found_by_geocode' === initialStatus.value) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetAddressByGeocode')}`;
    }
    
    // Define functions
    const inputAddress = () => {
        resetMessages();
    
        geolocationStore.updateField('latitude', null);
        geolocationStore.updateField('longitude', null);
        geolocationStore.updateField('formattedAddress', null);
    
        // Only activate request if not in edit mode
        if (!props.isEditMode) {
            activateRequestGeolocation.value = canRequestGeocodeByAddress();
        }
    }
    
    const inputGeocode = () => {
        resetMessages();
    
        geolocationStore.updateField('address', null);
        geolocationStore.updateField('zipCode', null);
        geolocationStore.updateField('city', null);
        geolocationStore.updateField('country', null);
        geolocationStore.updateField('formattedAddress', null);
    
        // Only activate request if not in edit mode
        if (!props.isEditMode) {
            activateRequestGeolocation.value = canRequestAddressByGeocode();
        }
    }
    
    const resetMessages = () => {
        warningRequestGeolocation.value = null;
        errorRequestGeolocation.value = null;
        addressFound.value = null;
    }
    
    const canRequestGeocodeByAddress = () => {
        if (isEmpty(geolocationStore.address)) {
            return false;
        }
    
        if (isEmpty(geolocationStore.zipCode)) {
            return false;
        }
    
        if (isEmpty(geolocationStore.city)) {
            return false;
        }
    
        if (isEmpty(geolocationStore.country)) {
            return false;
        }
    
        return true;
    }
    
    const canRequestAddressByGeocode = () => {
        if (isEmpty(geolocationStore.latitude)) {
            return false;
        }
    
        if (isEmpty(geolocationStore.longitude)) {
            return false;
        }
    
        return true;
    }
    
    const beforeRequestGeolocation = () => {
        resetMessages();
        requestingGeolocation.value = true;
    }
    
    const getAddressByGeocode = async () => {
        beforeRequestGeolocation();
    
        // Request
        const payload = {
            'latitude': parseFloat(geolocationStore.latitude as string),
            'longitude': parseFloat(geolocationStore.longitude as string),
            'isEditMode': props.isEditMode
        }
    
        try {
            const response =  await ApiService.post("geolocation/reverse-geocoding", payload);
            const responseData = response.data.data;
    
            if ('OK' === responseData.status) {
                geolocationStore.updateField('address', responseData.address);
                geolocationStore.updateField('zipCode', responseData.zipCode);
                geolocationStore.updateField('city', responseData.city);
                geolocationStore.updateField('country', responseData.countryCode);
                geolocationStore.updateField('formattedAddress', responseData.formattedAddress);
    
                if (null === responseData.address || null === responseData.zipCode || null === responseData.city) {
                    warningRequestGeolocation.value = `${t('ProductForm.Geolocation.noCompleteResultGetAddressByGeocode')}`;
                }
    
                activateRequestGeolocation.value = false;
            } else if ('ZERO_RESULTS' === responseData.status) {
                errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetAddressByGeocode')}`;
                activateRequestGeolocation.value = false;
            } else {
                errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetAddressByGeocode')}`;
            }
        } catch (error) {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetAddressByGeocode')}`;
        }
    
        requestingGeolocation.value = false;
    }
    
    // Function to ensure country is always a code, not a full name
    const ensureCountryCode = (country) => {
        // Mapping of country names to codes
        const countryMapping = {
            'France': 'FR',
            'Belgique': 'BE',
            'Suisse': 'CH',
            'Luxembourg': 'LU',
            'Allemagne': 'DE',
            'Italie': 'IT',
            'Espagne': 'ES'
        };
        
        // If country is a full name, convert it to code
        return countryMapping[country] || country;
    };

    const getGeocodeByAddress = async () => {
        beforeRequestGeolocation();

        // Ensure country is a code, not a full name
        const countryCode = ensureCountryCode(geolocationStore.country);

        // Request
        const payload = {
            'address': geolocationStore.address,
            'zipCode': geolocationStore.zipCode,
            'city': geolocationStore.city,
            'countryCode': countryCode,
            'isEditMode': props.isEditMode
        }
    
        try {
            const response =  await ApiService.post("geolocation/geocoding", payload);
            const geocode = response.data.data;
    
            if ('OK' === geocode.status) {
                geolocationStore.updateField('latitude', geocode.latitude);
                geolocationStore.updateField('longitude', geocode.longitude);
                geolocationStore.updateField('formattedAddress', geocode.formattedAddress);
    
                addressFound.value = geocode.formattedAddress;
                activateRequestGeolocation.value = false;
            } else if ('ZERO_RESULTS' === geocode.status) {
                errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetGeocodeByAddress')}`;
                activateRequestGeolocation.value = false;
            } else {
                errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetGeocodeByAddress')}`;
            }
        } catch (error) {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetGeocodeByAddress')}`;
        }
    
        requestingGeolocation.value = false;
    }
    
    // Define watcher
    watch(
        method,
        (value) => {
            resetMessages();

            geolocationStore.updateField('address', null);
            geolocationStore.updateField('zipCode', null);
            geolocationStore.updateField('city', null);
            geolocationStore.updateField('country', null);
            geolocationStore.updateField('formattedAddress', null);
            geolocationStore.updateField('latitude', null);
            geolocationStore.updateField('longitude', null);
            
            // Only activate request if not in edit mode
            if (!props.isEditMode) {
                if ('address' === value) {
                    activateRequestGeolocation.value = canRequestGeocodeByAddress();
                } else if ('geocoding' === value) {
                    activateRequestGeolocation.value = canRequestAddressByGeocode();
                }
            }
        }
    );
</script>

<style scoped lang="scss">
    .status-geolocation {
        margin-bottom: 10px;
    }
    
    .warning-geolocation {
        color: #ffb84d;
    }
    
    .error-geolocation {
        color: #ea0000;
    }
    
    .suggestion-formatted-address {
        margin-bottom: 15px;
    }
    
    .suggestion-formatted-address-alert {
        margin-top: 5px;
    }
    
    .edit-mode-notice {
        margin-top: 10px;
        margin-bottom: 10px;
        font-style: italic;
        color: #666;
    }
</style>
